import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { mockSuperAdmin } from './mockPrincipal';
import {
  failClosedTokenProvider,
  type AuthEnvironment,
  type AuthRepository,
  type AuthSession,
  type TokenProvider,
} from './types';

const AuthContext = createContext<AuthSession | null>(null);

export interface AuthActions {
  readonly signOut: () => Promise<void>;
}

const AuthActionsContext = createContext<AuthActions | null>(null);

export interface AuthProviderProps {
  readonly children: ReactNode;
  readonly environment: AuthEnvironment;
  readonly authRepository: AuthRepository;
  readonly tokenProvider?: TokenProvider;
}

export function AuthProvider({
  children,
  environment,
  authRepository,
  tokenProvider = failClosedTokenProvider,
}: AuthProviderProps) {
  const [session, setSession] = useState<AuthSession>({
    status: 'loading',
    principal: null,
    accessToken: null,
  });

  useEffect(() => {
    let active = true;

    async function resolveSession(): Promise<void> {
      if (environment.authMode === 'mock') {
        if (environment.environment !== 'dev') {
          if (active) setSession({ status: 'error', principal: null, accessToken: null });
          return;
        }
        if (active) {
          setSession({ status: 'authenticated', principal: mockSuperAdmin, accessToken: null });
        }
        return;
      }

      try {
        const token = await tokenProvider.getIdToken();
        if (token === null) {
          if (active) setSession({ status: 'unauthenticated', principal: null, accessToken: null });
          return;
        }

        const result = await authRepository.getCurrentUser(token);
        if (active) setSession({ status: 'authenticated', principal: result, accessToken: token });
      } catch {
        if (active) setSession({ status: 'error', principal: null, accessToken: null });
      }
    }

    void resolveSession();
    return () => {
      active = false;
    };
  }, [authRepository, environment, tokenProvider]);

  const actions = useMemo<AuthActions>(
    () => ({
      async signOut(): Promise<void> {
        // Only Cognito mode has a real identity-provider boundary to call. DEV Mock Auth has no
        // backend session to revoke, so signing out there is a purely local UI reset.
        if (environment.authMode === 'cognito') {
          try {
            await tokenProvider.signOut();
          } catch {
            // Fail closed: still drop the local session even if the identity provider sign-out
            // call itself failed. We never want to keep rendering an authenticated session after
            // sign-out was requested.
          }
        }
        setSession({ status: 'unauthenticated', principal: null, accessToken: null });
      },
    }),
    [environment.authMode, tokenProvider],
  );

  return (
    <AuthContext.Provider value={session}>
      <AuthActionsContext.Provider value={actions}>{children}</AuthActionsContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthSession {
  const session = useContext(AuthContext);
  if (session === null) throw new Error('useAuth must be used within AuthProvider');
  return session;
}

export function useAuthActions(): AuthActions {
  const actions = useContext(AuthActionsContext);
  if (actions === null) throw new Error('useAuthActions must be used within AuthProvider');
  return actions;
}
